import { LibrarySkeleton } from '@/components/Library/LibrarySkeleton';
import NoResults from '@/components/NoSearchResults/NoResults';
import { VIDEO_TILE_WIDTH, VideoTile } from '@/components/VideoTile';
import PlanUpgradePopUp, { EPopupType } from '@/pages/plan-upgrade-popup/PlanUpgradePopUp';

import { minAmountVideosPerMonthForDiscountByPlanGroup } from '@/pages/plan-upgrade-popup/MonthlyToYearlyUpgradeConsts';
import { VideoBulkActionsBar } from '@/pages/video-library/components/VideoBulkActionsBar';
import { CreateVideoTile } from '@/pages/video-studio/components/CreateVideoTile';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import { useCurrentLibraryItems, useFilterVideos, useGetProjects, VideoFilters, videoFilters } from '@/state/video-library/videoLibrary';
import { LibraryItemType } from '@d-id/types';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { ChangePlanModalWrapper } from './ChangePlanModalWrapper';
import { PostFirstCheckoutModal } from './LibraryPostFirstCheckoutModal';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
`;

const VideoContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${VIDEO_TILE_WIDTH}px, 1fr));
    grid-row: 1; /* Ensure each row takes up equal height */
    grid-gap: 24px;
    overflow: hidden;
`;

const EndOfPage = styled.div``;

export const VideoLibrary = () => {
    const { user, isAnonymous } = useAuth();
    const mixpanel = useAnalytics();
    const { ref, inView: endOfListInView } = useInView();

    const { fetchNextPage, isLoading, hasNextPage } = useGetProjects({ isAnonymous });
    const [isPostCheckoutOpen, setIsPostCheckoutOpen] = useState(false);
    const { state } = useLocation();
    const { isAfterGenerate } = (state as any) || {};
    const { userSettings } = useUserSettings();

    const { items: libraryItems } = useCurrentLibraryItems();
    const [filters, setFilters] = useRecoilState(videoFilters);
    const { getFilteredVideos } = useFilterVideos();
    const filteredLibraryItems = getFilteredVideos(libraryItems);

    // Track galleryLoad event once when the gallery page loads with data
    const hasTrackedGalleryLoad = useRef(false);
    useEffect(() => {
        if (!isLoading && !isAnonymous && libraryItems.length > 0 && !hasTrackedGalleryLoad.current) {
            const videomakerProjectsCount = libraryItems.filter(item => item.type === LibraryItemType.SIMPLESHOW).length;
            const editorProjectsCount = libraryItems.filter(item => item.type === LibraryItemType.PROJECT).length;

            mixpanel?.track('galleryLoad', {
                allProjectsCount: libraryItems.length,
                videomakerProjectsCount,
                editorProjectsCount,
            });
            hasTrackedGalleryLoad.current = true;
        }
    }, [isLoading, isAnonymous, libraryItems.length, mixpanel]);

    const planUpgradeUserFlag = useFlag('monthly_to_annual_popup_type', EPopupType.CONTROL);
    const isPlanUpgradeUserFlag =
        planUpgradeUserFlag === (EPopupType.A as EPopupType) || planUpgradeUserFlag === (EPopupType.B as EPopupType);

    const isPlanUpgradePopUp =
        user?.billingInterval === 'month' &&
        isPlanUpgradeUserFlag &&
        isAfterGenerate &&
        libraryItems.length > minAmountVideosPerMonthForDiscountByPlanGroup(user?.planGroup) &&
        !userSettings?.messages?.monthlyToAnnualDiscountOffer?.seen;

    useEffect(() => {
        if (endOfListInView && !isLoading) {
            fetchNextPage();
        }
    }, [endOfListInView, isLoading]);

    if (isLoading && !isAnonymous) {
        return <LibrarySkeleton amount={7} tileHeight="190px" tileWidth={`${VIDEO_TILE_WIDTH}px`} />;
    }

    return (
        <Container>
            {!isLoading && !libraryItems?.length ? (
                <>
                    <CreateVideoTile />
                </>
            ) : (
                <>
                    <NoResults
                        show={!filteredLibraryItems.length && !!filters.nameContains}
                        handleClearSearch={() => setFilters((filters: VideoFilters) => ({ ...filters, nameContains: '' }))}
                    />
                    <VideoContainer>
                        {filteredLibraryItems.map(item => (
                            <VideoTile key={item.id} item={item} origin="video-gallery" />
                        ))}
                    </VideoContainer>
                    {hasNextPage && <EndOfPage ref={ref} />}
                    <VideoBulkActionsBar />
                </>
            )}
            <PostFirstCheckoutModal isPostCheckoutOpen={isPostCheckoutOpen} setIsPostCheckoutOpen={setIsPostCheckoutOpen} />
            <ChangePlanModalWrapper
                openPostCheckout={() => {
                    setIsPostCheckoutOpen(true);
                }}
            />
            <PlanUpgradePopUp show={isPlanUpgradePopUp} />
        </Container>
    );
};
